import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Calendar, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function SocialSchedulerHub() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const isAdmin = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    try {
      const r = await api.get("/social-scheduler/lists");
      setLists(r.data.lists || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin]);

  const createList = async () => {
    if (!newName.trim()) return toast.error("Navn er påkrevd");
    try {
      const r = await api.post("/social-scheduler/lists", {
        name: newName.trim(),
        description: newDesc.trim() || null,
      });
      setShowNew(false);
      setNewName("");
      setNewDesc("");
      nav(`/admin/sosiale-medier/${r.data.list.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteList = async (id, name) => {
    if (!window.confirm(`Slette «${name}» og alt innhold i den? Kan ikke angres.`)) return;
    try {
      await api.delete(`/social-scheduler/lists/${id}`);
      setLists((prev) => prev.filter((l) => l.id !== id));
      toast.success("Liste slettet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Ikke tilgjengelig</h1>
          <p className="text-[var(--cream-soft)]">Sosiale-medier-planlegger er foreløpig admin-only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)]" data-testid="social-hub-title">
              Sosiale medier
            </h1>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mt-1 max-w-2xl">
              Lag lister av innlegg (tekst + media) og eksportér til Postpone og andre planleggere.
              Lister kan gjenbrukes så mange ganger du vil.
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="btn-magenta inline-flex items-center gap-2"
            data-testid="new-list-btn"
          >
            <Plus className="h-4 w-4" /> Ny liste
          </button>
        </div>

        {loading && <div className="font-meta text-[var(--cream-soft)]">Laster…</div>}

        {!loading && lists.length === 0 && (
          <div className="editorial-card p-10 text-center" data-testid="empty-state">
            <Calendar className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
            <div className="font-manrope text-sm text-[var(--cream)] mb-1">
              Ingen lister ennå
            </div>
            <div className="font-manrope text-xs text-[var(--cream-soft)]">
              Opprett din første for å komme i gang
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lists.map((l) => (
            <div
              key={l.id}
              className="editorial-card p-5 hover:border-[var(--magenta)] transition"
              data-testid={`list-card-${l.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <Link
                  to={`/admin/sosiale-medier/${l.id}`}
                  className="font-fraunces italic text-xl text-[var(--cream)] hover:text-[var(--magenta)] transition"
                  data-testid={`list-name-${l.id}`}
                >
                  {l.name}
                </Link>
                <button
                  onClick={() => deleteList(l.id, l.name)}
                  className="text-[var(--cream-faint)] hover:text-[var(--magenta)] shrink-0"
                  data-testid={`delete-list-${l.id}`}
                  title="Slett listen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {l.description && (
                <p className="font-manrope text-xs text-[var(--cream-soft)] mb-3 line-clamp-2">
                  {l.description}
                </p>
              )}
              <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{l.post_count || 0} innlegg</span>
                <span>·</span>
                <span>Eksportert {l.exported_count || 0}×</span>
                {l.last_exported_at && (
                  <>
                    <span>·</span>
                    <span title={l.last_exported_at}>
                      Sist: {new Date(l.last_exported_at).toLocaleDateString("nb-NO")}
                    </span>
                  </>
                )}
              </div>
              <Link
                to={`/admin/sosiale-medier/${l.id}`}
                className="font-meta text-xs tracking-[0.15em] text-[var(--magenta)] hover:underline inline-flex items-center gap-1 mt-3"
                data-testid={`open-list-${l.id}`}
              >
                Åpne <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>

        {/* Ny liste-modal */}
        {showNew && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNew(false)}
          >
            <div
              className="editorial-card p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">
                Ny liste
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[var(--cream-soft)]">Navn *</label>
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Batch juni 2026"
                    className="input-editorial w-full"
                    autoFocus
                    data-testid="new-list-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--cream-soft)]">Beskrivelse (valgfritt)</label>
                  <textarea
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    rows={2}
                    className="input-editorial w-full"
                    data-testid="new-list-desc"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={createList} className="btn-magenta flex-1" data-testid="create-list-btn">
                    Opprett
                  </button>
                  <button onClick={() => setShowNew(false)} className="btn-ghost flex-1">
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
